import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import GlobalAudioControls from '../../components/ui/GlobalAudioControls';
import ConnectionStatusIndicator from '../../components/ui/ConnectionStatusIndicator';
import QuickActionFloatingMenu from '../../components/ui/QuickActionFloatingMenu';
import ZodiacWheel from './components/ZodiacWheel';
import AudioPlayer from './components/AudioPlayer';
import PlanetaryMixer from './components/PlanetaryMixer';
import TimeRangeSelector from './components/TimeRangeSelector';
import ExportControls from './components/ExportControls';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AstrologicalAudioPlayer = () => {
  const [selectedPlanets, setSelectedPlanets] = useState(['Sun', 'Moon', 'Venus', 'Mars', 'Jupiter']);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState('Cosmic Harmony in A♭');
  const [currentTimeRange, setCurrentTimeRange] = useState('current');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState('combined'); // combined, wheel, mixer
  const [isLoading, setIsLoading] = useState(false);

  const tracks = [
    'Cosmic Harmony in A♭',
    'Planetary Dance in C Major',
    'Lunar Whispers in D Minor',
    'Solar Radiance in G',
    'Celestial Symphony in F#'
  ];

  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);

  useEffect(() => {
    setCurrentTrack(tracks[currentTrackIndex]);
  }, [currentTrackIndex]);

  const handlePlanetSelect = (planet) => {
    setSelectedPlanets(prev => {
      if (prev.includes(planet.name)) {
        return prev.filter(p => p !== planet.name);
      } else {
        return [...prev, planet.name];
      }
    });
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleNext = () => {
    setCurrentTrackIndex(prev => (prev + 1) % tracks.length);
  };

  const handlePrevious = () => {
    setCurrentTrackIndex(prev => (prev - 1 + tracks.length) % tracks.length);
  };

  const handleTimeRangeChange = (range, customData = null) => {
    setIsLoading(true);
    setCurrentTimeRange(range);
    
    // Simulate loading time for planetary calculations
    setTimeout(() => {
      setIsLoading(false);
      console.log('Time range changed to:', range, customData);
    }, 1500);
  };

  const handleHistoricalPlayback = (date, event) => {
    setIsLoading(true);
    console.log('Playing historical date:', date, event);
    
    setTimeout(() => {
      setIsLoading(false);
      setIsPlaying(true);
    }, 1000);
  };

  const handlePlanetToggle = (planetName) => {
    setSelectedPlanets(prev => {
      if (prev.includes(planetName)) {
        return prev.filter(p => p !== planetName);
      } else {
        return [...prev, planetName];
      }
    });
  };

  const handleIntensityChange = (planetName, intensity) => {
    console.log(`${planetName} intensity changed to ${intensity}%`);
  };

  const handleExport = (exportOptions) => {
    console.log('Exporting with options:', exportOptions);
    // Simulate export process
  };

  const handleShare = (shareType, shareData) => {
    console.log('Sharing:', shareType, shareData);
    // Handle different share types
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const toggleViewMode = () => {
    const modes = ['combined', 'wheel', 'mixer'];
    const currentIndex = modes.indexOf(viewMode);
    setViewMode(modes[(currentIndex + 1) % modes.length]);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Astrological Audio Player - AstroTone</title>
        <meta name="description" content="Transform planetary positions into personalized musical experiences with interactive zodiac visualization and audio controls." />
      </Helmet>

      <Header />

      <main className="pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center animate-gentle-pulse">
                <Icon name="Music" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-bold text-text-primary">
                  Astrological Audio Player
                </h1>
                <p className="text-text-secondary">
                  Transform planetary positions into personalized musical experiences
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <ConnectionStatusIndicator />
              <Button
                variant="ghost"
                iconName={viewMode === 'combined' ? 'Grid3x3' : viewMode === 'wheel' ? 'Circle' : 'Sliders'}
                onClick={toggleViewMode}
                className="w-10 h-10 p-0"
              />
              <Button
                variant="ghost"
                iconName={isFullscreen ? "Minimize" : "Maximize"}
                onClick={toggleFullscreen}
                className="w-10 h-10 p-0"
              />
              <ExportControls
                currentTrack={currentTrack}
                onExport={handleExport}
                onShare={handleShare}
              />
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="fixed inset-0 z-modal bg-background/80 backdrop-blur-cosmic flex items-center justify-center">
              <div className="glass bg-surface/95 backdrop-blur-cosmic rounded-xl p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4 animate-cosmic-glow">
                  <Icon name="Loader" size={24} color="white" className="animate-spin" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">
                  Calculating Planetary Positions
                </h3>
                <p className="text-text-secondary">
                  Generating your personalized cosmic audio experience...
                </p>
              </div>
            </div>
          )}

          {/* Time Range Selector */}
          <div className="mb-8">
            <TimeRangeSelector
              onTimeRangeChange={handleTimeRangeChange}
              onHistoricalPlayback={handleHistoricalPlayback}
            />
          </div>

          {/* Main Content */}
          <div className={`grid gap-8 ${
            isFullscreen ? 'fixed inset-0 z-fullscreen bg-background p-8 pt-24' : ''
          } ${
            viewMode === 'combined' ? 'lg:grid-cols-2' :
            viewMode === 'wheel'? 'lg:grid-cols-1' : 'lg:grid-cols-1'
          }`}>
            
            {/* Zodiac Wheel */}
            {(viewMode === 'combined' || viewMode === 'wheel') && (
              <div className={`${viewMode === 'wheel' ? 'max-w-2xl mx-auto' : ''}`}>
                <div className="glass bg-surface/95 backdrop-blur-cosmic rounded-xl p-6 border border-border">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                        <Icon name="Circle" size={16} color="white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-heading font-semibold text-text-primary">
                          Zodiac Wheel
                        </h2>
                        <p className="text-sm text-text-secondary">
                          Interactive planetary positions & circle of fifths
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-text-secondary font-mono">
                        {selectedPlanets.length} planets active
                      </span>
                      <Button
                        variant="ghost"
                        iconName="RotateCcw"
                        onClick={() => setSelectedPlanets([])}
                        className="w-8 h-8 p-0"
                      />
                    </div>
                  </div>
                  
                  <div className="aspect-square">
                    <ZodiacWheel
                      onPlanetSelect={handlePlanetSelect}
                      selectedPlanets={selectedPlanets}
                      planetPositions={{}}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Right Panel */}
            <div className="space-y-8">
              {/* Audio Player */}
              <AudioPlayer
                currentTrack={currentTrack}
                isPlaying={isPlaying}
                onPlayPause={handlePlayPause}
                onNext={handleNext}
                onPrevious={handlePrevious}
              />

              {/* Planetary Mixer */}
              {(viewMode === 'combined' || viewMode === 'mixer') && (
                <PlanetaryMixer
                  selectedPlanets={selectedPlanets}
                  onPlanetToggle={handlePlanetToggle}
                  onIntensityChange={handleIntensityChange}
                />
              )}
            </div>
          </div>

          {/* Current Status */}
          <div className="mt-8 glass bg-surface/95 backdrop-blur-cosmic rounded-xl p-6 border border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-heading font-bold text-primary mb-1">
                  {selectedPlanets.length}
                </div>
                <div className="text-sm text-text-secondary">Active Planets</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading font-bold text-secondary mb-1">
                  {currentTimeRange === 'current' ? 'Live' : 'Historical'}
                </div>
                <div className="text-sm text-text-secondary">Playback Mode</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading font-bold text-accent mb-1">
                  A♭
                </div>
                <div className="text-sm text-text-secondary">Current Key</div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <GlobalAudioControls />
      <QuickActionFloatingMenu />
    </div>
  );
};

export default AstrologicalAudioPlayer;